import type { TFunction } from '@frontend/i18n';
import { contextFactory } from '@frontend/design-system';
import type {
  PaymentMethodSetupAction,
  PaymentMethodSetupApi,
  PaymentMethodSetupErrorHandler,
  PaymentMethodSetupEventHandler,
  PaymentMethodSetupGateway,
  PaymentMethodSetupGatewayConfig,
  PaymentMethodSetupKind,
  PaymentMethodSetupSession,
  SavedPaymentMethod,
} from '../types';

/** `notice`: setup can't start (no session, unsupported gateway or element load error); `message` says why. */
export type PaymentMethodSetupView = 'notice' | 'form';

export type PaymentMethodSetupContextValue = {
  kind: PaymentMethodSetupKind;
  view: PaymentMethodSetupView;
  gateway?: PaymentMethodSetupGateway;
  primary: PaymentMethodSetupAction;
  secondary: PaymentMethodSetupAction;
  shouldShowLoading: boolean;
  loadingMessage?: string;
  message?: string;
};

export const [PaymentMethodSetupContext, usePaymentMethodSetupContext] = contextFactory<PaymentMethodSetupContextValue>(
  'usePaymentMethodSetupContext must be used inside PaymentMethodSetup'
);

export type PaymentMethodSetupProviderProps = React.PropsWithChildren<{
  kind: PaymentMethodSetupKind;
  api: PaymentMethodSetupApi;
  gatewayConfig: PaymentMethodSetupGatewayConfig;
  /** Matches a processor redirect back to this setup. */
  setupKey: string;
  /** Create it with `usePreparePaymentMethodSetup` before opening; without one, setup shows a notice. */
  session: PaymentMethodSetupSession | undefined;
  onSaved: (method: SavedPaymentMethod) => void;
  onCancel: () => void;
  /** Reports when a save is in flight so the host can block dismissal. */
  onSavingChange?: (isSaving: boolean) => void;
  /** Defaults to an error alert. */
  onError?: PaymentMethodSetupErrorHandler;
  onEvent?: PaymentMethodSetupEventHandler;
}>;

export type PaymentMethodSetupGatewayProps = Required<
  Omit<PaymentMethodSetupProviderProps, 'children' | 'onEvent' | 'session' | 'onSavingChange'>
> &
  React.PropsWithChildren<{
    session: PaymentMethodSetupSession;
    onEvent: PaymentMethodSetupEventHandler;
    onSavingChange?: (isSaving: boolean) => void;
  }>;

export const noop = () => undefined;

/** Setup can't continue: show `message`, keep Save disabled and let the user cancel. */
export const buildNoticeValue = ({
  kind,
  gateway,
  message,
  onCancel,
  t,
}: Pick<PaymentMethodSetupContextValue, 'kind' | 'gateway'> & {
  message: string;
  onCancel: () => void;
  t: TFunction;
}): PaymentMethodSetupContextValue => ({
  kind,
  gateway,
  view: 'notice',
  message,
  primary: { label: t('Save'), onClick: noop, disabled: true },
  secondary: { label: t('Cancel'), onClick: onCancel },
  shouldShowLoading: false,
});
